import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Divider, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import Filters from "../../Components/Filters"
import ManageColumn from "../../Components/ManageColumns"
import MobilePagination from "../../Components/MobilePagination/MobilePagination"
import Table from "../../Components/Table"
import Icon from "../../Icon"
import { useUpdatePreferences } from "../User/hooks/useUpdatePreferences"
import AddCommentModal from "./components/AddCommentModal"
import ConfirmModal from "./components/ConfirmModal"
import PerksList from "./components/List/PerksList"
import ManageOrderModal from "./components/ManageOrderModal/ManageOrderModal"
import PartnerModal from "./components/PartnerModal/PartnerModal"
import { getActionColumn, getColumns } from "./components/Table/columns"
import {
  clearPerksAdminFilters as clearPerksAdminFiltersAction,
  PERKS_ADMIN_PREFERENCES_VERSION,
  perksAdminSetProp as perksAdminSetPropAction,
  resetPerksAdminSaveResult as resetPerksAdminSaveResultAction,
  selectAreFiltersDefault
} from "./redux/perksAdmin.slice"
import {
  createPerkAdmin,
  deletePerkAdmin,
  deletePerkComment,
  getPerksAdmin,
  getPerksAdminFilterOptions,
  getPerksManageOrder,
  reorderPerks,
  savePerkComment,
  togglePerkVisibility,
  updatePerkAdmin
} from "./redux/perksAdmin.thunks"

import "./styles.less"

const { Title } = Typography

const pageSize = 15

const saveResultMessages = {
  perk_create: "Perk successfully created",
  perk_update: "Perk updated",
  perk_delete: "Perk deleted",
  perk_visibility: "Visibility updated",
  perk_comment: "Comment saved",
  perk_comment_delete: "Comment deleted",
  perk_reorder: "Partner card order successfully updated"
}

const PerksAdminPage = ({
  fetching,
  saveFetching,
  saveResult,
  result,
  filters,
  columns,
  manageOrderList,
  manageOrderFetching,
  filterOptions,
  visibleFilters,
  getPerksAdmin,
  getPerksAdminFilterOptions,
  createPerkAdmin,
  updatePerkAdmin,
  deletePerkAdmin,
  togglePerkVisibility,
  getPerksManageOrder,
  reorderPerks,
  savePerkComment,
  deletePerkComment,
  resetPerksAdminSaveResult,
  perksAdminSetProp,
  clearPerksAdminFilters
}) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const isFiltersDefault = useSelector(selectAreFiltersDefault)
  const isMobile = useSelector((state) => state.nav?.isMobile)

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState("-created")

  const [partnerModal, setPartnerModal] = useState({
    open: false,
    mode: "add",
    record: null
  })
  const [commentModal, setCommentModal] = useState({
    open: false,
    record: null
  })
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [confirmModal, setConfirmModal] = useState({ open: false })

  const { count, results } = result

  const preferencesData = useMemo(
    () => ({
      version: PERKS_ADMIN_PREFERENCES_VERSION,
      filters,
      columns,
      visibleFilters
    }),
    [filters, columns, visibleFilters]
  )
  useUpdatePreferences("perks-admin", preferencesData)

  const buildParams = useCallback(() => {
    const params = { page: pageNumber, page_size: pageSize, ordering }
    if (Array.isArray(filters?.partner_name) && filters.partner_name.length) {
      params.partner_name = filters.partner_name.join(",")
    }
    if (Array.isArray(filters?.created) && filters.created.length >= 2) {
      params.created = `${filters.created[0]},${filters.created[1]}`
    }
    return params
  }, [pageNumber, ordering, filters])

  const refetch = useCallback(() => {
    getPerksAdmin(buildParams())
  }, [buildParams, getPerksAdmin])

  const handleTableChange = (pagination, _filters, sorter) => {
    setPageNumber(pagination.current || 1)

    if (sorter?.field && sorter?.order) {
      const dir = sorter.order === "ascend" ? "" : "-"
      setOrdering(`${dir}${sorter.field}`)
    } else {
      setOrdering("-created")
    }
  }

  const handleToggleVisibility = useCallback(
    (record) => {
      setConfirmModal({
        open: true,
        kind: record.is_visible ? "deactivate" : "activate",
        record,
        onConfirm: () => togglePerkVisibility({ id: record.id })
      })
    },
    [togglePerkVisibility]
  )

  const handleRowAction = useCallback(
    (record, action) => {
      if (action === "edit") {
        setPartnerModal({ open: true, mode: "edit", record })
      } else if (action === "comment") {
        setCommentModal({ open: true, record })
      } else if (action === "delete") {
        setConfirmModal({
          open: true,
          kind: "delete",
          record,
          onConfirm: () => deletePerkAdmin({ id: record.id })
        })
      }
    },
    [deletePerkAdmin, togglePerkVisibility]
  )

  const columnsMap = useMemo(
    () =>
      getColumns({
        t,
        onSelectRow: handleRowAction,
        onToggleVisibility: handleToggleVisibility
      }),
    [t, handleRowAction, handleToggleVisibility]
  )

  const visibleColumns = useMemo(() => {
    const active = columns.filter((c) => c.active).map((c) => c.field_id)
    return [
      columnsMap.row_number,
      ...active.map((id) => columnsMap[id]).filter(Boolean),
      getActionColumn({ t, onSelectRow: handleRowAction })
    ]
  }, [columns, columnsMap, t, handleRowAction])

  const handlePartnerSubmit = (formData) => {
    if (partnerModal.mode === "edit") {
      updatePerkAdmin({ id: partnerModal.record.id, formData })
    } else {
      createPerkAdmin(formData)
    }
  }

  const handleCommentSubmit = (comment_text) => {
    if (!comment_text) {
      deletePerkComment({ id: commentModal.record.id })
    } else {
      savePerkComment({
        id: commentModal.record.id,
        comment_text
      })
    }
  }

  useEffect(() => {
    refetch()
  }, [buildParams])

  useEffect(() => {
    getPerksAdminFilterOptions()
  }, [])

  useEffect(() => {
    if (!saveResult) return

    notification.success({
      message: t(saveResultMessages[saveResult]) || t("Saved")
    })

    if (["perk_create", "perk_update", "perk_delete"].includes(saveResult)) {
      setPartnerModal({ open: false, mode: "add", record: null })
    }
    if (saveResult === "perk_comment" || saveResult === "perk_comment_delete") {
      setCommentModal({ open: false, record: null })
    }
    if (saveResult === "perk_reorder") {
      setOrderModalOpen(false)
    }
    if (saveResult === "perk_visibility" || saveResult === "perk_delete") {
      setConfirmModal({ open: false })
    }
    refetch()
    resetPerksAdminSaveResult()
  }, [saveResult, notification, resetPerksAdminSaveResult, refetch, t])

  return (
    <div className="perks-admin-page" data-testid="perks-admin-page">
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          {t("Perks & Benefits")}
        </Title>

        <Space>
          <Button
            icon={<Icon type="grab" />}
            data-testid="perks-admin-manage-order-btn"
            onClick={() => {
              getPerksManageOrder()
              setOrderModalOpen(true)
            }}
          >
            {t("Manage order")}
          </Button>
          <Button
            type="primary"
            icon={<Icon type="plus" />}
            data-testid="perks-admin-add-btn"
            onClick={() => setPartnerModal({ open: true, mode: "add", record: null })}
          >
            {t("Add partner")}
          </Button>
        </Space>
      </Flex>

      <Divider
        style={{
          margin: "20px -20px 20px -20px",
          width: "calc(100% + 40px)"
        }}
      />

      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Filters
          filtersType="perksAdmin"
          visibleFilters={visibleFilters || { partner_name: true, created: true }}
          filters={filters}
          filterOptions={{
            partner_name: filterOptions?.partner_name || []
          }}
          allFilters={filters}
          onChangeFilter={(newData, type) => {
            if (type === "changeVisible") {
              perksAdminSetProp("visibleFilters", newData)
            } else {
              perksAdminSetProp("filters", newData)
              setPageNumber(1)
            }
          }}
          withoutAdd
          showClearBtn={!isFiltersDefault}
          onClearFilters={() => {
            clearPerksAdminFilters()
            setPageNumber(1)
          }}
        />
        {!isMobile && (
          <ManageColumn
            myColumns={columns}
            iconsType="perksAdmin"
            pinnedColumns={["#"]}
            onChangeColumns={(value) => perksAdminSetProp("columns", value)}
          />
        )}
      </Flex>

      {isMobile ? (
        <>
          <PerksList
            data={results ?? []}
            loading={fetching}
            emptyComponent={<EmptyComponent />}
            onSelectRow={handleRowAction}
            onToggleVisibility={handleToggleVisibility}
          />

          {results?.length > 0 && (
            <MobilePagination
              current={pageNumber}
              total={count || 0}
              pageSize={pageSize}
              onChange={(page) => setPageNumber(page)}
            />
          )}
        </>
      ) : (
        <Table
          rowKey="id"
          loading={fetching}
          dataSource={results}
          columns={visibleColumns}
          pagination={{
            total: count,
            current: pageNumber,
            pageSize: pageSize
          }}
          onChange={handleTableChange}
          locale={{ emptyText: <EmptyComponent /> }}
          scroll={{ x: "max-content" }}
        />
      )}

      {partnerModal.open && (
        <PartnerModal
          open={partnerModal.open}
          mode={partnerModal.mode}
          initialValues={partnerModal.record}
          submitting={saveFetching}
          onSubmit={handlePartnerSubmit}
          onCancel={() => setPartnerModal({ open: false, mode: "add", record: null })}
        />
      )}

      {commentModal.open && (
        <AddCommentModal
          open={commentModal.open}
          initialValue={commentModal.record?.comment}
          submitting={saveFetching}
          onSubmit={handleCommentSubmit}
          onCancel={() => setCommentModal({ open: false, record: null })}
        />
      )}

      {orderModalOpen && (
        <ManageOrderModal
          open={orderModalOpen}
          items={manageOrderList}
          loading={manageOrderFetching}
          submitting={saveFetching}
          onSubmit={reorderPerks}
          onCancel={() => setOrderModalOpen(false)}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        kind={confirmModal.kind}
        record={confirmModal.record}
        loading={saveFetching}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  fetching: state.perksAdmin.fetching,
  saveFetching: state.perksAdmin.saveFetching,
  saveResult: state.perksAdmin.saveResult,
  result: state.perksAdmin.result,
  filters: state.perksAdmin.filters,
  columns: state.perksAdmin.columns,
  defaultColumns: state.perksAdmin.defaultColumns,
  manageOrderList: state.perksAdmin.manageOrderList,
  manageOrderFetching: state.perksAdmin.manageOrderFetching,
  filterOptions: state.perksAdmin.filterOptions,
  visibleFilters: state.perksAdmin.visibleFilters
})

const mapDispatchToProps = (dispatch) => ({
  getPerksAdmin: (params) => dispatch(getPerksAdmin(params)),
  getPerksAdminFilterOptions: () => dispatch(getPerksAdminFilterOptions()),
  createPerkAdmin: (formData) => dispatch(createPerkAdmin(formData)),
  updatePerkAdmin: (payload) => dispatch(updatePerkAdmin(payload)),
  deletePerkAdmin: (payload) => dispatch(deletePerkAdmin(payload)),
  togglePerkVisibility: (payload) => dispatch(togglePerkVisibility(payload)),
  getPerksManageOrder: () => dispatch(getPerksManageOrder()),
  reorderPerks: (data) => dispatch(reorderPerks(data)),
  savePerkComment: (payload) => dispatch(savePerkComment(payload)),
  deletePerkComment: (payload) => dispatch(deletePerkComment(payload)),

  resetPerksAdminSaveResult: () => dispatch(resetPerksAdminSaveResultAction()),
  perksAdminSetProp: (key, value) => dispatch(perksAdminSetPropAction({ key, value })),
  clearPerksAdminFilters: () => dispatch(clearPerksAdminFiltersAction())
})

export default connect(mapStateToProps, mapDispatchToProps)(PerksAdminPage)
